import { err, ok } from '@orbit-mind/shared-types';
import * as doc0 from './document';
import * as node0 from './node';
/** Is this command legal against this document? Never mutates. */
export function validate(doc, cmd) {
    switch (cmd.kind) {
        case 'create-node':
            return node0.validateCreateNode(doc, cmd.payload);
        case 'delete-node':
            return node0.validateDeleteNode(doc, cmd.payload);
        case 'restore-nodes':
            return node0.validateRestoreNodes(doc, cmd.payload);
        case 'move-node':
            return node0.validateMoveNode(doc, cmd.payload);
        case 'reparent-node':
            return node0.validateReparentNode(doc, cmd.payload);
        case 'reorder-node':
            return node0.validateReorderNode(doc, cmd.payload);
        case 'change-node-side':
            return node0.validateChangeNodeSide(doc, cmd.payload);
        case 'update-node':
            return node0.validateUpdateNode(doc, cmd.payload);
        case 'update-node-text':
            return node0.validateUpdateNodeText(doc, cmd.payload);
        case 'update-node-style':
            return node0.validateUpdateNodeStyle(doc, cmd.payload);
        case 'set-collapsed':
            return node0.validateSetCollapsed(doc, cmd.payload);
        case 'add-attachment':
        case 'remove-attachment':
            return node0.validateAttachment(doc, cmd.payload);
        case 'create-edge':
            return doc0.validateCreateEdge(doc, cmd.payload);
        case 'delete-edge':
            return doc0.validateDeleteEdge(doc, cmd.payload);
        case 'restore-edges':
            return doc0.validateRestoreEdges(doc, cmd.payload);
        case 'update-edge':
            return doc0.validateUpdateEdge(doc, cmd.payload);
        case 'apply-layout':
            return doc0.validateApplyLayout();
        case 'clear-branch-layout':
            return doc0.validateClearBranchLayout(doc, cmd.payload);
        case 'apply-theme':
            return doc0.validateApplyTheme();
        case 'rename-map':
            return doc0.validateRenameMap(doc, cmd.payload);
        case 'group-nodes':
            return doc0.validateGroupNodes(doc, cmd.payload);
        case 'ungroup-nodes':
            return doc0.validateUngroupNodes(doc, cmd.payload);
    }
}
/**
 * Applies a command, assuming it has already been validated.
 *
 * Separate from `apply` so a batch — a whole-branch side change, say — can validate every step
 * up front and then execute without re-checking each one.
 */
export function execute(doc, cmd) {
    const meta = cmd.meta;
    switch (cmd.kind) {
        case 'create-node':
            return node0.executeCreateNode(doc, cmd.payload, meta);
        case 'delete-node':
            return node0.executeDeleteNode(doc, cmd.payload, meta);
        case 'restore-nodes':
            return node0.executeRestoreNodes(doc, cmd.payload, meta);
        case 'move-node':
            return node0.executeMoveNode(doc, cmd.payload, meta);
        case 'reparent-node':
            return node0.executeReparentNode(doc, cmd.payload, meta);
        case 'reorder-node':
            return node0.executeReorderNode(doc, cmd.payload, meta);
        case 'change-node-side':
            return node0.executeChangeNodeSide(doc, cmd.payload, meta);
        case 'update-node':
            return node0.executeUpdateNode(doc, cmd.payload, meta);
        case 'update-node-text':
            return node0.executeUpdateNodeText(doc, cmd.payload, meta);
        case 'update-node-style':
            return node0.executeUpdateNodeStyle(doc, cmd.payload, meta);
        case 'set-collapsed':
            return node0.executeSetCollapsed(doc, cmd.payload, meta);
        case 'add-attachment':
            return node0.executeAddAttachment(doc, cmd.payload, meta);
        case 'remove-attachment':
            return node0.executeRemoveAttachment(doc, cmd.payload, meta);
        case 'create-edge':
            return doc0.executeCreateEdge(doc, cmd.payload, meta);
        case 'delete-edge':
            return doc0.executeDeleteEdge(doc, cmd.payload, meta);
        case 'restore-edges':
            return doc0.executeRestoreEdges(doc, cmd.payload, meta);
        case 'update-edge':
            return doc0.executeUpdateEdge(doc, cmd.payload, meta);
        case 'apply-layout':
            return doc0.executeApplyLayout(doc, cmd.payload, meta);
        case 'clear-branch-layout':
            return doc0.executeClearBranchLayout(doc, cmd.payload, meta);
        case 'apply-theme':
            return doc0.executeApplyTheme(doc, cmd.payload, meta);
        case 'rename-map':
            return doc0.executeRenameMap(doc, cmd.payload, meta);
        case 'group-nodes':
            return doc0.executeGroupNodes(doc, cmd.payload, meta);
        case 'ungroup-nodes':
            return doc0.executeUngroupNodes(doc, cmd.payload, meta);
    }
}
/**
 * Validates and executes in one step.
 *
 * The normal entry point. A refused command leaves the document untouched and returns why —
 * never a partially applied edit, which is the state nothing downstream knows how to recover
 * from.
 */
export function apply(doc, cmd) {
    const check = validate(doc, cmd);
    if (!check.ok)
        return err(check.error);
    return ok(execute(doc, cmd));
}
/**
 * Applies a sequence, stopping at the first refusal.
 *
 * All-or-nothing: on failure the original document is returned, not a half-applied one. A batch
 * is a single user act — grouping a selection, flipping a branch — and half of it is not a state
 * the user asked for or could make sense of.
 */
export function applyAll(doc, commands) {
    let current = doc;
    const inverses = [];
    for (const cmd of commands) {
        const result = apply(current, cmd);
        if (!result.ok)
            return err(result.error);
        current = result.value.document;
        // Reversed as we go: undoing a batch means undoing its steps in the opposite order.
        inverses.unshift(result.value.inverse);
    }
    return ok({ document: current, inverses });
}
//# sourceMappingURL=index.js.map